'use client';

import type { BillingCycle } from '@/lib/api';
import { CYCLE_LABEL } from '@/lib/billing-cycle';
import { usePlatformLabelResolver } from '@/lib/platform-labels';
import type { CycleOption } from './pricing-model';

/**
 * Segmented control de ciclo (Mensal / Semestral / Anual) no topo dos cards.
 * Cada opcao mostra o rotulo do ciclo + o marcador de desconto representativo
 * (maior desconto disponivel naquele ciclo). So renderiza quando ha mais de
 * uma opcao - sem ciclo longo configurado, a vitrine fica so mensal e o
 * toggle some.
 */
export function CycleToggle({
  options,
  value,
  onChange,
}: {
  options: CycleOption[];
  value: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}) {
  const label = usePlatformLabelResolver();

  if (options.length <= 1) return null;

  return (
    <div
      role="tablist"
      aria-label="Ciclo de cobrança"
      className="inline-flex items-center gap-1 p-1.5 rounded-2xl bg-[color:var(--surface-sunken)] border border-[color:var(--border-default)] shadow-sm"
    >
      {options.map((opt) => {
        const active = opt.cycle === value;
        const discountText =
          opt.maxDiscountPercent != null
            ? opt.discountVaries
              ? label('pricing.toggle.discount-varies', `até -${opt.maxDiscountPercent}%`, {
                  percent: opt.maxDiscountPercent,
                })
              : label('pricing.toggle.discount', `-${opt.maxDiscountPercent}%`, {
                  percent: opt.maxDiscountPercent,
                })
            : null;
        return (
          <button
            key={opt.cycle}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.cycle)}
            className={`flex flex-col items-center justify-center min-w-[100px] px-5 py-2 rounded-xl transition-all ${
              active
                ? 'bg-[color:var(--brand)] text-[color:var(--text-on-brand)] shadow-md'
                : 'text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]'
            }`}
          >
            <span className="text-sm font-semibold leading-tight">{CYCLE_LABEL[opt.cycle]}</span>
            {discountText && (
              <span
                className={`text-[11px] font-bold mt-0.5 ${
                  active ? 'text-[color:var(--text-on-brand)]' : 'text-[color:var(--warning)]'
                }`}
              >
                {discountText}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
